import { useEffect, useState } from "react";
import {
    ListContainer,
    ListContext,
    PAGE_DEFAULT,
    PAGE_SIZE_UNLIMITED,
    Page,
    PageRoute,
    SearchBar,
    buildTemplatePath,
} from "@compass/core";

import { ListFooter } from "components";
import { ListFilterBase, Permission, IEPListDto, IEPListResource } from "generated/openApi";
import { useInitListFilter, useList } from "hooks";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { IEPListPageHeader } from "./IEPListPageHeader";
import { IEPTable } from "./IEPTable";

const routeName = "iep";

export const iepListPageRoute: PageRoute = {
    handle: "IEP",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildTemplatePath(routeName),
};

export const IEPListPage = () => {
    const ROOT_PERMISSION = Permission.MatchUtility;

    const [listItems, setListItems] = useState<IEPListDto[]>([]);

    const { listFilter, setListFilter } = useInitListFilter<ListFilterBase>({
        sortObjects: [{ property: "name", isDesc: false }],
    });

    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<IEPListResource | null>(null);
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter,
        setListFilter,
        fetchList: async (listFilter) => {
            const data = await ApiService.iep.getAll();
            setListItems(data || []);
        },
        fetchTotal: async (listFilter) => {
            const data = await ApiService.iep.getAll();
            setTotal(data?.length || 0);
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.iep.getListResource()
            .then(setResource)
            .catch((err) => {
                console.error("Failed to fetch list resource", err);
            });
    }, []);

    return (
        <Page header={<IEPListPageHeader rootPermission={ROOT_PERMISSION} handle={iepListPageRoute.handle} />}>
            <ListContainer
                fullHeight
                header={<SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />}
                tableContainer={
                    resource && (
                        <IEPTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            ieps={listItems}
                            setIeps={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    )
                }
                footer={
                    <ListFooter
                        total={total}
                        size={listFilter.pageSize}
                        page={listFilter.page}
                        onPageChange={applyPage}
                        onSizeChange={applyPageSize}
                        onSortMethodChange={applySortMethod}
                    />
                }
            />
            <ListContext.Provider
                value={{
                    loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
                }}
            >
                <Outlet />
            </ListContext.Provider>
        </Page>
    );
};